import { useCallback, useState } from 'react'
import Form from 'antd/lib/form'
import { VerifiedUser, LockOpen } from '@mui/icons-material'

import { useGetUsersForReporting, useExportUsersForReporting } from 'services/auth/auth.service'
import { useNavigate } from 'react-router-dom'
import { StyledDiv, StyledIRESSVerificationStatus, StyledIRESSVerificationStatusIcon } from 'pages/Reporting/Reporting.styles'
import { commonUtils } from 'utils'

const generateUsersColumns = () => {
  return [
    {
      name: 'fullName',
      header: 'Full Name',
      defaultFlex: 2,
      type: 'text',
    },
    {
      name: 'email',
      header: 'Email',
      defaultFlex: 2,
      type: 'text',
    },
    {
      name: 'phoneNumber',
      header: 'Phone Number',
      defaultFlex: 2,
      type: 'text',
      render: ({ data }: any) => {
        return <StyledDiv>{data.phoneNumber}</StyledDiv>
      },
    },
    {
      name: 'latestClientCreatedAt',
      header: 'Latest Client Created At',
      defaultFlex: 2,
      type: 'text',
      render: ({ data }: any) => {
        return <StyledDiv>{data.latestClientCreatedAt ? commonUtils.formatDate(data.latestClientCreatedAt) : 'N/A'}</StyledDiv>
      },
    },
    {
      name: 'latestQuoteCreated',
      header: 'Latest Quote Created At',
      defaultFlex: 2,
      type: 'text',
      render: ({ data }: any) => {
        return <StyledDiv>{data.latestQuoteCreated ? commonUtils.formatDate(data.latestQuoteCreated) : 'N/A'}</StyledDiv>
      },
    },
    {
      name: 'clientsCount',
      header: 'Clients Count',
      defaultFlex: 1,
      type: 'text',
      render: ({ data }: any) => {
        return <StyledDiv>{data.clientsCount || 0}</StyledDiv>
      },
    },
    {
      name: 'isDirectlyAuthorised',
      header: 'Directly Authorised',
      defaultFlex: 1,
      type: 'text',
      render: ({ data }: any) => {
        return <StyledDiv>{data.isDirectlyAuthorised ? 'Yes' : 'No'}</StyledDiv>
      },
    },
    {
      name: 'srDownloadedCount',
      header: 'SR Downloaded Count',
      defaultFlex: 1,
      type: 'text',
      render: ({ data }: any) => {
        return (
          <StyledDiv>
            {data.srDownloadedCount || 0} / {data.clientsCount || 0}
          </StyledDiv>
        )
      },
    },
    {
      name: 'isIressCredentialsVerified',
      header: 'Iress Credentials Verified',
      defaultFlex: 2,
      type: 'text',
      render: ({ data }: any) => {
        return (
          <StyledDiv>
            <StyledIRESSVerificationStatus verified={data.isIressCredentialsVerified}>
              <StyledIRESSVerificationStatusIcon>
                {data.isIressCredentialsVerified ? <VerifiedUser style={{ fontSize: '16px' }} /> : <LockOpen style={{ fontSize: '16px' }} />}
              </StyledIRESSVerificationStatusIcon>
              {data.isIressCredentialsVerified ? 'Verified' : 'Not Verified'}
            </StyledIRESSVerificationStatus>
          </StyledDiv>
        )
      },
    },
  ]
}

const useReportingPage = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  // Note: Company drawers's form fields
  const [formData, setFormData] = useState<{
    email: string
    isIressCredentialsVerified: boolean | null
    clientCountTimePeriod: string | null
    clientsMaxCount: number | null
    clientsMinCount: number | null
  }>({
    email: '',
    isIressCredentialsVerified: null,
    clientCountTimePeriod: null,
    clientsMaxCount: null,
    clientsMinCount: null,
  })

  const limit = 10
  const { users, refetch, count } = useGetUsersForReporting({
    skip: 0,
    limit,
  })

  const { refetch: exportRefetch } = useExportUsersForReporting({
    filter: formData,
  })

  const usersColumns = generateUsersColumns()

  const usersDataSource = users.map((user: any) => {
    return {
      id: user._id,
      fullName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      phoneNumber: user.phoneNumber,
      isIressCredentialsVerified: user.isIressCredentialsVerified,
      clientsCount: user.clientsCount,
      isDirectlyAuthorised: user.isDirectlyAuthorised,
      latestClientCreatedAt: user.latestClientCreatedAt,
      latestQuoteCreated: user.latestQuoteCreated,
      srDownloadedCount: user.srDownloadedCount,
    }
  })

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const clearDrawerData = () => {
    setFormData({
      email: '',
      isIressCredentialsVerified: null,
      clientCountTimePeriod: null,
      clientsMaxCount: null,
      clientsMinCount: null,
    })
    form.setFieldsValue({
      email: '',
      isIressCredentialsVerified: null,
      clientCountTimePeriod: null,
      clientsMaxCount: null,
      clientsMinCount: null,
    })
  }

  const closeDrawer = () => {
    setOpen(false)
    clearDrawerData()
  }

  const handleSubmit = async () => {
    setLoading(true)

    const shouldProvideClientsMinCount = !(formData.clientsMinCount === null || !String(formData.clientsMinCount)?.length)
    const shouldProvideClientsMaxCount = !(formData.clientsMaxCount === null || !String(formData.clientsMaxCount)?.length)

    refetch({
      filter: {
        ...formData,
        clientsMinCount: shouldProvideClientsMinCount ? Number(formData.clientsMinCount) : null,
        clientsMaxCount: shouldProvideClientsMaxCount ? Number(formData.clientsMaxCount) : null,
      },
    })

    setLoading(false)
    setOpen(false)
  }

  const handleFormFieldChange = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    if ((name === 'clientsMaxCount' || name === 'clientsMinCount') && value?.length === 0 && !value) {
      setFormData((prevState: any) => ({ ...prevState, [name]: null }))
    }
    setFormData((prevState: any) => ({ ...prevState, [name]: value }))
  }

  const onRowClick = useCallback((rowProps: any, event: any) => {
    const user_id = rowProps.data.id
    navigate(`/user-profile/${user_id}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterClick = () => {
    setOpen(true)
  }

  const handleExportClick = async () => {
    setLoading(true)

    const shouldProvideClientsMinCount = !(formData.clientsMinCount === null || !String(formData.clientsMinCount)?.length)
    const shouldProvideClientsMaxCount = !(formData.clientsMaxCount === null || !String(formData.clientsMaxCount)?.length)

    try {
      const result = await exportRefetch({
        filter: {
          ...formData,
          clientsMinCount: shouldProvideClientsMinCount ? Number(formData.clientsMinCount) : null,
          clientsMaxCount: shouldProvideClientsMaxCount ? Number(formData.clientsMaxCount) : null,
        },
      })

      if (result.data?.exportUsersForReporting) {
        // The backend returns base64-encoded Excel data
        const base64Data = result.data.exportUsersForReporting

        // Convert base64 to binary more efficiently
        const binaryString = atob(base64Data)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        // Create blob and download
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'perc_reporting_users.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return {
    form,
    usersColumns,
    usersDataSource,
    refetch,
    count,
    limit,
    closeDrawer,
    formData,
    open,
    loading,
    handleSubmit,
    handleFormFieldChange,
    onRowClick,
    handleFilterClick,
    handleSelectChange,
    handleExportClick,
  }
}

export default useReportingPage
